import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'

import type { ChangeEvent, ReactElement } from 'react'

import VanillaTextInput from '../../Basic/TextInput'
import Wrapper from '../Wrapper'

import Label from '~/components/Label'

import './styles.scss'

interface Props {
  className?: string
  defaultValue?: { disabled: boolean; value: string }[]
  disabled?: boolean
  error?: string
  label?: string
  onChange?: (data: string[]) => void
  placeholder?: string
  size?: string
}

const MultiTextInput = ({ className, defaultValue, error, label, onChange, ...props }: Props): ReactElement => {
  const inputContainerRef = useRef<HTMLDivElement | null>(null)
  const [isInputAdded, setIsInputAdded] = useState(false)
  const [inputsValues, setInputsValues] = useState<{ disabled: boolean; value: string }[]>([])

  const multiTextInputClasses = classNames(className, 'multi-text-input')

  const handleChange = (event: ChangeEvent<HTMLInputElement>, index: number) => {
    const newValues = [...inputsValues]
    newValues[index].value = event.target.value
    setInputsValues(newValues)
    onChange && onChange(newValues.map(({ value }) => value))
  }

  const handleAddButtonClick = () => {
    setIsInputAdded(true)
    setInputsValues([...inputsValues, { disabled: false, value: '' }])
  }

  const handleRemoveButtonClick = (index: number) => {
    const filteredValues = inputsValues.filter((_, i) => i !== index)
    setInputsValues(filteredValues.length > 0 ? filteredValues : [{ disabled: false, value: '' }])
    onChange && onChange(filteredValues.map(({ value }) => value))
  }

  useEffect(() => {
    if (defaultValue && defaultValue.length > 0) {
      setInputsValues([...defaultValue])
    } else {
      setInputsValues([{ disabled: false, value: '' }])
    }
  }, [defaultValue])

  useEffect(() => {
    if (inputContainerRef.current && isInputAdded) {
      inputContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [inputsValues, isInputAdded])

  return (
    <Wrapper className={multiTextInputClasses} error={error}>
      <div className="multi-text-input__header">
        <Label label={label || ''} />
        <div className="multi-text-input__header__add" onClick={handleAddButtonClick}>
          +
        </div>
      </div>
      <div className="multi-text-input__inputs" ref={inputContainerRef}>
        {inputsValues.map(({ disabled, value }, index) => (
          <div className="multi-text-input__inputs__input" key={index}>
            <VanillaTextInput
              {...props}
              disabled={disabled}
              onChange={(event) => handleChange(event, index)}
              size="medium"
              value={value}
            />
            {!disabled ? (
              <div className="multi-text-input__inputs__input__remove" onClick={() => handleRemoveButtonClick(index)}>
                -
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </Wrapper>
  )
}

export default MultiTextInput
